import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell } from "lucide-react";
import { api, fmtDateTime, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader, Spinner, Stat, EmptyState } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";

export default function DashboardPage() {
  const { user, tenant } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const v = d.vehicles, c = d.compliance, i = d.issues;
  return (
    <div>
      <PageHeader title={`Good day, ${user.name.split(" ")[0]}`} subtitle={`${tenant?.name} · fleet health at a glance`} testId="dashboard-header" />
      {d.commissioning && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="commissioning-banner">
          <Bell className="h-4 w-4 shrink-0 text-primary" />
          <span><strong>Commissioning mode</strong> — reminders are visible in-app but notification dispatch is paused until an Admin activates it in Settings.</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Vehicles" value={v.total} sub={`${v.ready_to_drive} ready · ${v.under_maintenance} under maintenance`} testId="stat-vehicles" />
        <Stat label="Compliance expired" value={c.expired} sub={`${c.expiring_soon} expiring soon`} tone={c.expired ? "danger" : "ok"} testId="stat-compliance" />
        <Stat label="Open issues" value={i.reported + i.acknowledged + i.in_progress + i.awaiting_part} sub={`${i.awaiting_part} awaiting part · ${i.recurring_open} recurring`} tone={i.reported ? "warn" : undefined} testId="stat-issues" />
        <Stat label="Service overdue" value={d.services.overdue} sub={`${d.services.due_soon} due soon`} tone={d.services.overdue ? "danger" : undefined} testId="stat-services" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card title="Attention needed" className="lg:col-span-3" testId="dashboard-reminders" action={<Link to="/reminders" data-testid="dashboard-view-reminders" className="text-sm font-medium text-primary hover:underline">All reminders</Link>}>
          {d.reminders.length === 0 ? <EmptyState title="Nothing due" description="No compliance or service items need attention right now." /> : (
            <ul className="divide-y">
              {d.reminders.map((r) => (
                <li key={r.key}>
                  <Link to={`/vehicles/${r.vehicle_id}`} className="fv-dense-row -mx-3" data-testid={`reminder-row-${r.key}`}>
                    <StatusBadge status={r.severity} />
                    <span className="min-w-0 flex-1 truncate">{r.message}</span>
                    <span className="fv-caption hidden sm:inline">Stage {r.stage}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Recent activity" className="lg:col-span-2" testId="dashboard-activity">
          {d.activity.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
            <ul className="space-y-3">
              {d.activity.map((a) => (
                <li key={a.id} className="text-sm">
                  <p className="font-medium">{titleCase(a.action.replace(".", " "))}</p>
                  <p className="text-xs text-muted-foreground">{a.user_name || "System"} · {fmtDateTime(a.created_at)}{a.meta?.registration_no ? ` · ${a.meta.registration_no}` : ""}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
